import { User } from "../types/User";

export type SubscriptionStatusKey =
  | "active"
  | "expiring"
  | "expired"
  | "inactive";

export type SubscriptionStatus = {
  key: SubscriptionStatusKey;
  /** Human label (IT) for tags / badges. */
  label: string;
  /** AntD-compatible color token (Tag/Badge `color`). */
  color: string;
  /** CSS hex used for the status dot in the toolbar header. */
  hex: string;
  /** Days until expiry (negative = already expired); null when no deadline. */
  daysLeft: number | null;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// "Expiring soon" window — within this many days from today the subscription
// is flagged so the trainer can renew before the athlete loses access.
export const EXPIRING_SOON_DAYS = 7;

/** Whole-day difference between an expiry date and today (local midnight). */
export function daysUntil(expiring: string | Date): number {
  const exp = new Date(expiring);
  const today = new Date();
  const expMidnight = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate());
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return Math.round((expMidnight.getTime() - todayMidnight.getTime()) / MS_PER_DAY);
}

/**
 * Single source of truth for how a user's subscription reads in the UI.
 *
 * - inactive: the account is disabled (no access regardless of the deadline)
 * - expired:  active flag set but the deadline is in the past
 * - expiring: active and within EXPIRING_SOON_DAYS of the deadline
 * - active:   active with a comfortable deadline (or no deadline at all)
 */
export function getSubscriptionStatus(
  user: Pick<User, "isActive" | "expiring">
): SubscriptionStatus {
  if (!user.isActive) {
    return {
      key: "inactive",
      label: "Non attivo",
      color: "default",
      hex: "#8c8c8c",
      daysLeft: user.expiring ? daysUntil(user.expiring) : null,
    };
  }

  if (!user.expiring) {
    return {
      key: "active",
      label: "Attivo",
      color: "success",
      hex: "#52c41a",
      daysLeft: null,
    };
  }

  const daysLeft = daysUntil(user.expiring);
  if (daysLeft < 0) {
    return {
      key: "expired",
      label: "Scaduto",
      color: "error",
      hex: "#ff4d4f",
      daysLeft,
    };
  }
  if (daysLeft <= EXPIRING_SOON_DAYS) {
    return {
      key: "expiring",
      label: daysLeft === 0 ? "Scade oggi" : `In scadenza (${daysLeft}g)`,
      color: "warning",
      hex: "#faad14",
      daysLeft,
    };
  }
  return {
    key: "active",
    label: "Attivo",
    color: "success",
    hex: "#52c41a",
    daysLeft,
  };
}

/** Locale-formatted expiry date, or a placeholder when unset. */
export function formatExpiry(expiring?: string | null): string {
  if (!expiring) return "Nessuna scadenza";
  return new Date(expiring).toLocaleDateString("it-IT");
}
